import { useEffect, useState } from 'react'
import { SidebarFooter, SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '@/components/ui/sidebar'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent } from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { LogOut, Settings, Lock } from 'lucide-react'
import { getCachedUser, getCurrentUser } from '@/lib/auth'
import type { UserDirectoryRecord } from '@/types/user-directory'
import { ChangePasswordDialog } from '@/components/users/ChangePasswordDialog'
import * as userApi from '@/lib/userApi'
import { useToast } from '@/hooks/use-toast'

interface SidebarAccountMenuProps {
  initials: string
  onLogin: () => void
  onLogout: () => void
}

export default function SidebarAccountMenu({ initials, onLogin, onLogout }: SidebarAccountMenuProps) {
  const [user, setUser] = useState<UserDirectoryRecord | null>(null)
  const [loading, setLoading] = useState(true)
  const [changePasswordOpen, setChangePasswordOpen] = useState(false)
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const { toast } = useToast()

  useEffect(() => {
    async function loadUser() {
      try {
        // Try cached user first
        const cached = getCachedUser()
        if (cached) {
          setUser(cached)
          setLoading(false)
          return
        }

        // If no cache, fetch from API
        const currentUser = await getCurrentUser()
        setUser(currentUser)
      } catch (error) {
        console.error('Failed to load user:', error)
        setUser(null)
      } finally {
        setLoading(false)
      }
    }

    loadUser()
  }, [])

  const handleChangePassword = async (currentPassword: string, newPassword: string) => {
    setIsChangingPassword(true)
    try {
      await userApi.changePassword(currentPassword, newPassword)
      toast({
        title: 'Contraseña actualizada',
        description: 'Tu contraseña ha sido cambiada exitosamente.',
        variant: 'default',
      })
      setChangePasswordOpen(false)
    } catch (error: any) {
      toast({
        title: 'Error al cambiar contraseña',
        description: error.message || 'No se pudo cambiar la contraseña. Verifica tu contraseña actual.',
        variant: 'destructive',
      })
    } finally {
      setIsChangingPassword(false)
    }
  }
  // Display name and email logic
  const displayName = loading
    ? 'Cargando...'
    : user
      ? user.fullName || user.username || 'User Account'
      : 'User Account'

  const displayEmail = loading
    ? '...'
    : user?.email || 'user@example.com'

  return (
    <SidebarFooter className="border-t">
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              >
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarFallback className="rounded-lg bg-primary text-primary-foreground">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                  <span className="truncate font-semibold">{displayName}</span>
                  <span className="truncate text-xs text-muted-foreground">{displayEmail}</span>
                </div>
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
              side="bottom"
              align="end"
              sideOffset={4}
            >
              <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                  <Avatar className="h-8 w-8 rounded-lg">
                    <AvatarFallback className="rounded-lg bg-primary text-primary-foreground">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">{displayName}</span>
                    <span className="truncate text-xs text-muted-foreground">{displayEmail}</span>
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />

              {/* Show "Change Password" only for local auth users (not Azure CIAM / Google OAuth) */}
              {user && (!user.authProvider || user.authProvider === 'local') && (
                <>
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                      <Settings className="h-4 w-4" />
                      <span>My Settings</span>
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent>
                      <DropdownMenuItem onClick={() => setChangePasswordOpen(true)}>
                        <Lock className="h-4 w-4" />
                        <span>Cambiar Mi Clave</span>
                      </DropdownMenuItem>
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                  <DropdownMenuSeparator />
                </>
              )}
              {/* Logout always visible - this component only renders inside authenticated layout */}
              <DropdownMenuItem onClick={onLogout}>
                <LogOut className="h-4 w-4" />
                <span>Logout</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>

      {/* Change Password Dialog */}
      <ChangePasswordDialog
        open={changePasswordOpen}
        onOpenChange={setChangePasswordOpen}
        onSubmit={handleChangePassword}
        isLoading={isChangingPassword}
      />
    </SidebarFooter>
  )
}
